import React, { useEffect, useState, useCallback } from 'react';
import { Box, Alert, CircularProgress, Button } from '@mui/material';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface ReCaptchaProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  action: string; // Required for Enterprise
  disabled?: boolean;
  className?: string;
  autoExecute?: boolean; // Auto-execute on load
}

export const ReCaptcha: React.FC<ReCaptchaProps> = ({
  siteKey,
  onVerify,
  onError,
  action,
  disabled = false,
  className,
  autoExecute = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  // Load reCAPTCHA Enterprise script
  useEffect(() => {
    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src*="recaptcha/enterprise.js"]`);
    if (existingScript || (window.grecaptcha && window.grecaptcha.enterprise)) {
      setIsScriptLoaded(true);
      setIsLoading(false);
      return;
    }

    // Load reCAPTCHA Enterprise script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    const handleLoad = () => {
      // Wait a bit for grecaptcha.enterprise to be available
      setTimeout(() => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          setIsScriptLoaded(true);
          setIsLoading(false);
        } else {
          setError('reCAPTCHA Enterprise not available after script load');
          setIsLoading(false);
          onError?.('reCAPTCHA Enterprise not available after script load');
        }
      }, 100);
    };

    const handleError = () => {
      setError('Failed to load reCAPTCHA Enterprise script');
      setIsLoading(false);
      onError?.('Failed to load reCAPTCHA Enterprise script');
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.head.appendChild(script);

    return () => {
      // Cleanup
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [siteKey, onError]);

  // Auto-execute when script is loaded and autoExecute is true

  // Execute reCAPTCHA Enterprise
  const executeRecaptcha = useCallback(async () => {
    if (!isScriptLoaded || disabled) {
      return;
    }

    // Double-check that grecaptcha.enterprise is available
    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      setError('reCAPTCHA Enterprise not loaded');
      onError?.('reCAPTCHA Enterprise not loaded');
      return;
    }

    setIsExecuting(true);
    setError(null);

    try {
      // Use a promise-based approach for better error handling
      const token = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise.execute(siteKey, { action })
            .then(resolve)
            .catch(reject);
        });
      });
      
      console.log('reCAPTCHA Enterprise executed successfully');
      setIsVerified(true);
      onVerify(token);
    } catch (err) {
      const errorMsg = `reCAPTCHA verification failed: ${err instanceof Error ? err.message : String(err)}`;
      console.error(errorMsg, err);
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsExecuting(false);
    }
  }, [action, disabled, isScriptLoaded, onError, onVerify, siteKey]);
  useEffect(() => {
    if (isScriptLoaded && autoExecute && !disabled && !isVerified && !isExecuting) {
      executeRecaptcha();
    }
  }, [isScriptLoaded, autoExecute, disabled, isVerified, isExecuting, executeRecaptcha]);

  if (isLoading) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        className={className}
        sx={{ py: 2 }}
      >
        <CircularProgress size={24} />
        <Box sx={{ ml: 2 }}>Loading reCAPTCHA...</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className={className}>
        {error}
      </Alert>
    );
  }

  if (autoExecute) {
    return (
      <Box className={className} sx={{ py: 1 }}>
        {isExecuting && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <span>Verifying with reCAPTCHA...</span>
          </Box>
        )}
        {isVerified && (
          <Alert severity="success">
            ✓ reCAPTCHA verification successful
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box className={className}>
      <Button
        variant="outlined"
        onClick={executeRecaptcha}
        disabled={disabled || isExecuting || !isScriptLoaded || isVerified}
        fullWidth
        sx={{ py: 1.5 }}
      >
        {isExecuting ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Verifying...
          </>
        ) : isVerified ? (
          '✓ Verified'
        ) : (
          'Verify with reCAPTCHA'
        )}
      </Button>
    </Box>
  );
};

export default ReCaptcha;